import { Checkbox, FormControlLabel, FormLabel, Grid, Radio, RadioGroup } from '@mui/material';
import { DividerTitle } from 'components/common/DividerTitle';
import { ValidatedDropdown } from 'components/common/ValidatedDropdown';
import { useFormikContext } from 'formik';
import { ValidatedTextField } from 'components/common/ValidatedTextField';
import { useSelector } from 'react-redux';
import { selectVIData } from 'selectors/videoIdentificationSelector';
import { getName } from 'country-list';
import moment from 'moment';
import { IconPopover } from 'components/common/IconPopover';
import { VideoIdentificationFormType, VideoLanguages } from 'types';

type Props = {
  businessRelationshipNumbers?: string[];
  nationalities?: string[];
  mobileNumbers?: string[];
  eMailAddresses?: string[];
};

export const VideoIdentificationForm = ({
  businessRelationshipNumbers = [],
  nationalities = [],
  mobileNumbers = [],
  eMailAddresses = [],
}: Props) => {
  const data = useSelector(selectVIData);
  const { values, setFieldValue, handleChange } = useFormikContext<VideoIdentificationFormType>();
  const infoText =
    'Note: The selected nationality determines which passport needs to be used by the prospect/client in the identification process.';

  return (
    <Grid data-testid="video-identification-form" container spacing={2}>
      <Grid item xs={12}>
        <DividerTitle text={'Business relationship'} />
      </Grid>
      <Grid item xs={6}>
        {businessRelationshipNumbers?.length <= 1 ? (
          <ValidatedTextField label={'BR'} name={'businessRelationshipNumbers'} maxChar={200} disabled />
        ) : (
          <ValidatedDropdown
            label={'BRs*'}
            name={'businessRelationshipNumbers'}
            options={businessRelationshipNumbers.map((x: string) => {
              return { label: x, value: x };
            })}
          />
        )}
      </Grid>

      <Grid item xs={12}>
        <DividerTitle text={'Partner data'} />
      </Grid>

      <Grid item xs={4}>
        <ValidatedTextField label={'First name'} name={'name.firstName'} maxChar={200} disabled />
      </Grid>
      <Grid item xs={4}>
        <ValidatedTextField label={'Middle name'} name={'name.middleName'} maxChar={200} disabled />
      </Grid>
      <Grid item xs={4}>
        <ValidatedTextField label={'Last name'} name={'name.lastName'} maxChar={200} disabled />
      </Grid>

      <Grid item xs={4}>
        {nationalities?.length <= 1 ? (
          <ValidatedTextField
            label={'Nationality'}
            name={'nationalities'}
            maxChar={200}
            disabled
            customValue={nationalities[0] ? getName(nationalities[0]) : ''}
            labelComponent={<IconPopover text={infoText} />}
          ></ValidatedTextField>
        ) : (
          <ValidatedDropdown
            label={'Nationality*'}
            name={'nationalities'}
            labelComponent={<IconPopover text={infoText} />}
            options={
              data?.nationalities?.map((nat: string) => {
                return { label: getName(nat) ?? nat, value: nat };
              }) ?? []
            }
          />
        )}
      </Grid>
      <Grid item xs={4}>
        <ValidatedTextField
          label={'Date of birth'}
          name={'dateOfBirth'}
          maxChar={200}
          disabled
          customValue={moment(values?.dateOfBirth).format('DD.MM.YYYY')}
        />
      </Grid>
      <Grid item xs={4}>
        <ValidatedTextField label={'Gender'} name={'name.gender'} maxChar={200} disabled />
      </Grid>

      <Grid item xs={12}>
        <DividerTitle text={'Main domicile'} />
      </Grid>

      <Grid item xs={6}>
        <ValidatedTextField
          label={'Country'}
          name={'address.country'}
          maxChar={200}
          disabled
          customValue={getName(values?.address.country ?? '')}
        />
      </Grid>
      <Grid item xs={6}>
        <ValidatedTextField label={'City'} name={'address.city'} maxChar={200} disabled />
      </Grid>

      <Grid item xs={6}>
        <ValidatedTextField label={'Street'} name={'address.street'} maxChar={200} disabled />
      </Grid>
      <Grid item xs={6}>
        <ValidatedTextField label={'Zip code'} name={'address.zip'} maxChar={200} disabled />
      </Grid>

      <Grid item xs={12}>
        <DividerTitle text={'Contact'} />
      </Grid>

      <Grid item xs={6}>
        {eMailAddresses?.length <= 1 ? (
          <ValidatedTextField label={'E-Mail'} name={'eMailAddresses'} maxChar={200} disabled />
        ) : (
          <ValidatedDropdown
            label={'E-Mail*'}
            name={'eMailAddresses'}
            options={eMailAddresses.map((x: string) => {
              return { label: x, value: x };
            })}
          />
        )}
      </Grid>
      <Grid item xs={6}>
        {mobileNumbers?.length <= 1 ? (
          <ValidatedTextField label={'Mobile'} name={'mobileNumbers'} maxChar={200} disabled />
        ) : (
          <ValidatedDropdown
            label={'Mobile*'}
            name={'mobileNumbers'}
            options={mobileNumbers.map((x: string) => {
              return { label: x, value: x };
            })}
          />
        )}
      </Grid>
      <Grid item xs={12}>
        <DividerTitle text={'Relationship manager'} />
      </Grid>

      <Grid item xs={6}>
        <ValidatedTextField label={'Name'} name={'clmUserInfo.fullName'} maxChar={200} disabled />
      </Grid>
      <Grid item xs={6}>
        <ValidatedTextField label={'E-Mail'} name={'clmUserInfo.emailAddress'} maxChar={200} disabled />
      </Grid>

      <Grid item xs={12}>
        <DividerTitle text={'Distribution language'} />
      </Grid>

      <Grid item xs={12}>
        <FormLabel id={'memberzone-email-language-label'}>Language</FormLabel>
        <RadioGroup
          row
          aria-label="Language"
          name="language"
          value={values.language}
          onChange={(event) => setFieldValue('language', event.target.value)}
        >
          {Object.values(VideoLanguages).map((value, index) => (
            <FormControlLabel
              value={value}
              control={<Radio id={`memberzone-radio-${value}`} color={'primary'} />}
              label={value}
              key={`email-language-${value}-${index}`}
            />
          ))}
        </RadioGroup>
      </Grid>

      <Grid item xs={12}>
        <FormControlLabel
          control={
            <Checkbox
              name="legalInformation"
              checked={values.legalInformation}
              onChange={handleChange}
              id="legal-information-checkbox"
              color="primary"
            />
          }
          label="Prospect has received the legal information on the use of the video identification and accepted it by e-mail"
        />
      </Grid>

      <Grid item xs={12}>
        <FormControlLabel
          control={
            <Checkbox
              name="generalBankingConditions"
              checked={values.generalBankingConditions}
              onChange={handleChange}
              id="general-banking-condition-checkbox"
              color="primary"
            />
          }
          label="The account holder confirms to have taken notice of the detailed information in the General Banking Conditions regarding the use of electronic communication means and to agree with these terms. "
        />
      </Grid>

      <Grid item xs={12}>
        <FormControlLabel
          control={
            <Checkbox
              name="advisoryLocationConfirmation"
              checked={values.advisoryLocationConfirmation}
              onChange={handleChange}
              id="advisory-location-confirmation-checkbox"
              color="primary"
            />
          }
          label="I confirm that the partner is advised out of one of the following advisory locations: Switzerland, Dubai, Israel or Uruguay."
        />
      </Grid>
    </Grid>
  );
};
